import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../firebase";
import { getStoredDeviceIdSync } from "../utils/deviceId";

const PENDING_SHARED_CLAIMS_KEY = "inssa:pending-shared-capsule-claims:v1";

function safeNow() {
  return Date.now();
}

function safeJsonParse(raw, fallback) {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function readPendingClaims() {
  try {
    const raw = localStorage.getItem(PENDING_SHARED_CLAIMS_KEY);
    const parsed = safeJsonParse(raw || "[]", []);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writePendingClaims(claims) {
  try {
    localStorage.setItem(PENDING_SHARED_CLAIMS_KEY, JSON.stringify(claims));
  } catch {
    // Ignore storage failures to avoid blocking share flow.
  }
}

function normalizeClaimKey(capsuleId, token) {
  return `${String(capsuleId || "").trim()}::${String(token || "").trim()}`;
}

function isPermanentClaimError(code) {
  const normalized = String(code || "").toLowerCase();
  return [
    "not-found",
    "permission-denied",
    "failed-precondition",
    "invalid-argument",
    "functions/not-found",
    "functions/permission-denied",
    "functions/failed-precondition",
    "functions/invalid-argument",
  ].includes(normalized);
}

export function queuePendingSharedCapsuleClaim({ capsuleId, token, path = "" }) {
  const cleanCapsuleId = String(capsuleId || "").trim();
  const cleanToken = String(token || "").trim();
  if (!cleanCapsuleId || !cleanToken) return;

  const now = safeNow();
  const nextEntry = {
    capsuleId: cleanCapsuleId,
    token: cleanToken,
    path: String(path || "").trim(),
    firstSeenAt: now,
    lastSeenAt: now,
  };

  const existing = readPendingClaims();
  const key = normalizeClaimKey(cleanCapsuleId, cleanToken);
  const index = existing.findIndex(
    (entry) => normalizeClaimKey(entry?.capsuleId, entry?.token) === key
  );

  if (index === -1) {
    writePendingClaims([...existing, nextEntry]);
    return;
  }

  const prev = existing[index] || {};
  const merged = {
    ...prev,
    capsuleId: cleanCapsuleId,
    token: cleanToken,
    path: String(path || prev.path || "").trim(),
    firstSeenAt: Number(prev.firstSeenAt) || now,
    lastSeenAt: now,
  };
  const next = [...existing];
  next[index] = merged;
  writePendingClaims(next);
}

export async function claimPendingSharedCapsules() {
  const uid = auth.currentUser?.uid;
  if (!uid) return { attempted: 0, claimed: 0 };

  const pending = readPendingClaims();
  if (!pending.length) return { attempted: 0, claimed: 0 };

  const claimFn = httpsCallable(functions, "claimSharedCapsule");
  const anonId = getStoredDeviceIdSync() || undefined;
  const successKeys = new Set();
  const permanentFailureKeys = new Set();

  await Promise.all(
    pending.map(async (entry) => {
      const capsuleId = String(entry?.capsuleId || "").trim();
      const token = String(entry?.token || "").trim();
      if (!capsuleId || !token) return;
      const key = normalizeClaimKey(capsuleId, token);
      try {
        await claimFn({ capsuleId, token, anonId });
        successKeys.add(key);
      } catch (error) {
        const code = String(error?.code || "").toLowerCase();
        if (isPermanentClaimError(code)) {
          permanentFailureKeys.add(key);
        }
      }
    })
  );

  if (successKeys.size || permanentFailureKeys.size) {
    const next = pending.filter((entry) => {
      const key = normalizeClaimKey(entry?.capsuleId, entry?.token);
      return !successKeys.has(key) && !permanentFailureKeys.has(key);
    });
    writePendingClaims(next);
  }

  return { attempted: pending.length, claimed: successKeys.size };
}

